import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import EditorEmail from '../components/EditorEmail';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

// BPO RH → Financeiro: e-mail ao financeiro do cliente com o documento de um
// pagamento (rescisão, férias ou recorrência mensal — FGTS, guias). O PDF
// anexado passa por conferência (CPF/CNPJ,
// nome e Gemini) antes de sair: documento de outra pessoa ou de outra empresa
// BLOQUEIA o envio — é o vazamento (LGPD) que esta tela existe para impedir.
// Prévia obrigatória, como em todo envio do operador.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const TIPOS = { rescisao: 'Rescisão', ferias: 'Férias', recorrencia: 'Recorrência mensal' };

// estado do mês de uma recorrência: enviada (verde) / atrasada (vermelho) /
// vence em até 3 dias (amarelo) / neutro — a mesma régua do backend
const EstadoRecorrencia = ({ r }) => {
  if (r.enviado) {
    return (
      <span className="badge" style={{ background: '#e8f5e9', color: '#116b47', border: '1px solid #116b47' }}
        title={`Enviada ao financeiro em ${r.enviado.em}${r.enviado.por ? ` por ${r.enviado.por}` : ''}`}>
        <i className="fas fa-circle-check me-1" aria-hidden="true"></i>
        enviada em {r.enviado.em}{r.enviado.por ? ` por ${r.enviado.por}` : ''}
      </span>
    );
  }
  const d = r.dias_para_prazo;
  if (d === null || d === undefined) return <span className="text-muted small">—</span>;
  if (d < 0) {
    return (
      <span className="badge" style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #a11d2b33' }}>
        <i className="fas fa-circle-exclamation me-1" aria-hidden="true"></i>atrasada há {Math.abs(d)} dia{Math.abs(d) === 1 ? '' : 's'}
      </span>
    );
  }
  if (d <= 3) {
    return (
      <span className="badge" style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f1be48' }}>
        <i className={`fas ${d === 0 ? 'fa-bell' : 'fa-triangle-exclamation'} me-1`} aria-hidden="true"></i>
        {d === 0 ? 'vence hoje' : `vence em ${d} dia${d === 1 ? '' : 's'}`}
      </span>
    );
  }
  return <span className="text-muted small">vence em {d} dias</span>;
};

const SeloIAOff = () => (
  <span className="badge ms-2" style={{ background: '#f1f3f5', color: '#6c757d', border: '1px solid #dee2e6' }}
    title="Conferência por IA desligada para esta recorrência — o anexo vai sem conferência automática">
    <i className="fas fa-wand-magic-sparkles me-1" aria-hidden="true"></i>IA off
  </span>
);

const Veredito = ({ rotulo, valor, semNome = false }) => {
  const cfg = valor === true ? { cor: '#116b47', bg: '#e6f7ef', icone: 'fa-circle-check', txt: 'confere' }
    : valor === false ? { cor: '#a11d2b', bg: '#fdeaec', icone: 'fa-circle-xmark', txt: 'DIVERGE' }
      // guia da empresa (GFD/GRRF): não há empregado no documento para conferir
      : semNome ? { cor: '#6c757d', bg: '#f1f3f5', icone: 'fa-file-invoice', txt: 'sem nome no documento' }
        : { cor: '#6c757d', bg: '#f1f3f5', icone: 'fa-circle-question', txt: 'inconclusivo' };
  return (
    <span className="badge me-1" style={{ background: cfg.bg, color: cfg.cor, border: `1px solid ${cfg.cor}33`, fontWeight: 600 }}>
      <i className={`fas ${cfg.icone} me-1`}></i>{rotulo}: {cfg.txt}
    </span>
  );
};

const Laudo = ({ laudo }) => {
  if (!laudo) return <span className="text-muted small">sem laudo</span>;
  // recorrência com a IA desligada: o anexo não passa por conferência — só o selo
  if (laudo.sem_ia) {
    return (
      <div className="small">
        <SeloIAOff />
        <span className="text-muted ms-2">{laudo.resumo || 'anexado sem conferência automática'}</span>
      </div>
    );
  }
  return (
    <div className="small">
      {/* laudo de recorrência não tem pessoa (documento da empresa) */}
      {laudo.pessoa !== undefined && <Veredito rotulo="Pessoa" valor={laudo.pessoa} semNome={!!laudo.pessoa_sem_nome} />}
      <Veredito rotulo="Empresa" valor={laudo.empresa} />
      <Veredito rotulo="Tipo" valor={laudo.tipo} />
      {laudo.bloqueia && (
        <div className="mt-1 fw-semibold" style={{ color: '#a11d2b' }}>
          <i className="fas fa-ban me-1"></i>Bloqueado: {laudo.motivo_bloqueio}
        </div>
      )}
      {!laudo.bloqueia && (laudo.ressalvas || []).length > 0 && (
        <div className="mt-1" style={{ color: '#8a6d00' }}>
          <i className="fas fa-triangle-exclamation me-1"></i>{laudo.ressalvas.join('; ')}
        </div>
      )}
      {laudo.ia && (laudo.ia.colaborador_no_documento || laudo.ia.empresa_no_documento || laudo.ia.tipo_documento) && (
        <div className="text-muted mt-1">
          Lido no documento: {[laudo.ia.tipo_documento, laudo.ia.colaborador_no_documento, laudo.ia.empresa_no_documento].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
};

export default function BpoRhFinanceiro() {
  const [visao, setVisao] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');

  // ?cliente=<id> (link do Painel do Operador) abre direto a empresa
  const [clienteId, setClienteId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('cliente') || ''; } catch { return ''; }
  });
  const [detalhe, setDetalhe] = useState(null);
  // enviados ao financeiro ficam escondidos por padrão (pedido de 08/10/2026)
  const [mostrarEnviados, setMostrarEnviados] = useState({ rescisao: false, ferias: false });
  const [contato, setContato] = useState({ emails: '', copia: '' });
  const [salvandoContato, setSalvandoContato] = useState(false);

  const [caso, setCaso] = useState(null);          // caso escolhido (colaborador ou recorrência)
  const [anexos, setAnexos] = useState([]);        // anexos conferidos deste caso
  const [subindo, setSubindo] = useState(false);
  const [envio, setEnvio] = useState(null);        // { previa, form, confirmar, enviando, erro }

  // recorrências mensais (modal de gestão)
  const [recModal, setRecModal] = useState(false);
  const [recForm, setRecForm] = useState(null);      // null = sem formulário aberto
  const [recSalvando, setRecSalvando] = useState(false);
  const [recConfirma, setRecConfirma] = useState(null); // id aguardando confirmação de exclusão
  const [recErro, setRecErro] = useState('');

  const carregarVisao = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/bporh/financeiro/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setVisao(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar'))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregarVisao(); }, [carregarVisao]);

  const carregarDetalhe = useCallback((cid) => {
    if (!cid) { setDetalhe(null); return; }
    axios.get(`${API}/api/bporh/financeiro/${cid}/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => {
        setDetalhe(r.data);
        setContato({ emails: r.data.contato?.emails || '', copia: r.data.contato?.copia || '' });
      })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar a empresa'));
  }, []);
  useEffect(() => { setCaso(null); setAnexos([]); setEnvio(null); carregarDetalhe(clienteId); }, [clienteId, carregarDetalhe]);

  const salvarContato = () => {
    setSalvandoContato(true);
    axios.post(`${API}/api/bporh/financeiro/${clienteId}/`, { acao: 'contato', ...contato }, { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); carregarVisao(); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar'))
      .finally(() => setSalvandoContato(false));
  };

  // recorrências: criar/editar/excluir — o backend devolve a lista atualizada
  const postRecorrencia = (corpo, depois) => {
    setRecSalvando(true); setRecErro('');
    axios.post(`${API}/api/bporh/financeiro/${clienteId}/`, { acao: 'recorrencia', ...corpo }, { headers: headers() })
      .then((r) => {
        setDetalhe((d) => (d ? { ...d, recorrencias: r.data.recorrencias || [] } : d));
        setMsg(r.data.mensagem);
        carregarVisao(); // atualiza o "N recorrência(s) pendente(s)" da visão geral
        if (depois) depois();
      })
      .catch((e) => setRecErro(e.response?.data?.mensagem || 'Não foi possível salvar a recorrência'))
      .finally(() => setRecSalvando(false));
  };
  const recFormValido = recForm && (recForm.titulo || '').trim() !== ''
    && Number(recForm.dia) >= 1 && Number(recForm.dia) <= 31;
  const salvarRecorrencia = () => {
    if (!recFormValido) return;
    postRecorrencia({
      ...(recForm.id ? { id: recForm.id } : {}),
      titulo: recForm.titulo.trim(), dia: Number(recForm.dia),
      conferir_ia: recForm.conferir_ia ? 1 : 0, instrucao: (recForm.instrucao || '').trim(),
      ativo: recForm.ativo ? 1 : 0,
    }, () => setRecForm(null));
  };
  const excluirRecorrencia = (id) => postRecorrencia({ id, excluir: true }, () => setRecConfirma(null));

  const escolherCaso = (c) => {
    setCaso(c); setEnvio(null);
    // anexos já conferidos deste caso (a operadora pode ter fechado a tela);
    // recorrência casa pelo recorrencia_id, os demais pelo convenia_id
    setAnexos((detalhe?.anexos_pendentes || []).filter((a) => a.tipo === c.tipo
      && (c.tipo === 'recorrencia'
        ? String(a.recorrencia_id) === String(c.recorrencia_id)
        : String(a.convenia_id) === String(c.convenia_id))));
  };

  const anexar = (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!file || !caso) return;
    const fd = new FormData();
    fd.append('arquivo', file); fd.append('tipo', caso.tipo);
    if (caso.tipo === 'recorrencia') fd.append('recorrencia_id', caso.recorrencia_id);
    else fd.append('convenia_id', caso.convenia_id);
    setSubindo(true); setEnvio(null);
    axios.post(`${API}/api/bporh/financeiro/${clienteId}/anexar/`, fd, { headers: headers() })
      .then((r) => { setAnexos((a) => [...a, r.data.anexo]); setMsg(r.data.mensagem); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível anexar'))
      .finally(() => setSubindo(false));
  };
  const removerAnexo = (a) => {
    axios.delete(`${API}/api/bporh/financeiro/${clienteId}/anexo/${a.id}/`, { headers: headers() })
      .then(() => { setAnexos((l) => l.filter((x) => x.id !== a.id)); setEnvio(null); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível remover'));
  };

  const corpoEnvio = (form, enviar) => ({
    tipo: caso.tipo,
    ...(caso.tipo === 'recorrencia'
      ? { recorrencia_id: caso.recorrencia_id }
      : { convenia_id: caso.convenia_id, referencia: caso.referencia || null }),
    anexo_ids: anexos.map((a) => a.id), enviar,
    ...(form ? { operador_id: Number(form.operador_id) || undefined, destinatarios: form.destinatarios, copia: form.copia,
      assunto: form.assunto, conteudo_html: form.conteudo_html, confirmar_ressalvas: !!form.confirmar, confirmar_datas: !!form.confirmarDatas } : {}),
  });
  const montarPrevia = (form) => {
    setEnvio((s) => ({ ...(s || {}), carregando: true, erro: null }));
    axios.post(`${API}/api/bporh/financeiro/${clienteId}/email/`, corpoEnvio(form, false), { headers: headers() })
      .then((r) => {
        const p = r.data.previa;
        setEnvio({ previa: p, carregando: false, enviando: false,
          form: form || { operador_id: p.operador_id || '', destinatarios: p.destinatarios || '', copia: p.copia || '',
            assunto: p.assunto, conteudo_html: p.conteudo_html, confirmar: false } });
      })
      .catch((e) => setEnvio({ carregando: false, erro: e.response?.data?.mensagem || 'Não foi possível montar a prévia' }));
  };
  const enviar = () => {
    setEnvio((s) => ({ ...s, enviando: true, erro: null }));
    axios.post(`${API}/api/bporh/financeiro/${clienteId}/email/`, corpoEnvio(envio.form, true), { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); setEnvio(null); setCaso(null); setAnexos([]); carregarDetalhe(clienteId); carregarVisao(); })
      .catch((e) => setEnvio((s) => ({ ...s, enviando: false, erro: e.response?.data?.mensagem || 'Não foi possível enviar' })));
  };

  const bloqueado = anexos.some((a) => a.laudo?.bloqueia);
  const ressalvas = anexos.flatMap((a) => a.laudo?.ressalvas || []);
  const previaEmMemoria = envio?.previa && !envio.carregando;
  // qualquer alteração no formulário invalida a prévia mostrada — o envio usa o que está na tela
  const mudarForm = (patch) => setEnvio((s) => ({ ...s, form: { ...s.form, ...patch }, previaDesatualizada: true }));

  const clienteSel = (visao?.clientes || []).find((c) => String(c.cliente_id) === String(clienteId));
  const historico = detalhe?.historico || visao?.historico || [];

  return (
    <div className="painel-op fin-compacto">
      {/* nesta tela os cabeçalhos navy ocupavam demais (pedido de 14/10/2026) */}
      <style>{`
        .fin-compacto .po-card-head { min-height: 38px; padding: 6px 14px; }
        .fin-compacto .po-card-head h3 { font-size: 14.5px; gap: 6px; }
        .fin-compacto .po-card-head .po-sub { font-size: 11.5px; }
        .fin-compacto .po-card-head .badge { font-size: 11px; }
        .fin-compacto .po-card-head .btn-light { min-height: 28px; padding: .1rem .55rem; }
      `}</style>
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-money-check-dollar header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">BPO RH — Financeiro{clienteSel && <span className="text-muted"> · {clienteSel.cliente}</span>}</h2>
          </div>
          <div className="gr-toolbar">
            <label className="gr-campo" title="Empresa">
              <i className="fas fa-building" aria-hidden="true"></i>
              <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} style={{ border: 0, outline: 0, background: 'transparent', height: 34, fontSize: 13.5, minWidth: 200 }}>
                <option value="">Selecione a empresa...</option>
                {(visao?.clientes || []).map((c) => (
                  <option key={c.cliente_id} value={c.cliente_id}>
                    {c.cliente}{(c.rescisoes || c.ferias) ? ` · ${c.rescisoes} resc. / ${c.ferias} férias` : ''}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="gr-btn" onClick={() => { carregarVisao(); carregarDetalhe(clienteId); }} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
          </div>
        </div>

        <div className="alert alert-light border small py-2">
          <i className="fas fa-shield-halved me-2 text-primary"></i>
          Envio do documento de pagamento ao financeiro do cliente — rescisão, férias e recorrências mensais (FGTS, guias).
          O PDF anexado é conferido antes de sair:
          <b> pessoa</b> (CPF e nome), <b>empresa</b> (CNPJ ou razão social) e <b>tipo</b> (rescisão ou férias).
          Documento de outra pessoa ou de outra empresa <b>não sai</b>. Nada é enviado sem prévia.
        </div>
        {erro && <div className="alert alert-danger py-2" onClick={() => setErro('')} role="button"><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" onClick={() => setMsg('')} role="button"><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        {carregando && !visao && (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i>
            <div className="fs-5">Carregando as empresas e os pagamentos a comunicar...</div>
          </div>
        )}
        {clienteId && !detalhe && (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i>
            <div className="fs-5">Levantando rescisões e férias da empresa...</div>
          </div>
        )}

        {!clienteId && visao && (
          <section className="po-card po-enter">
            <header className="po-card-head">
              <h3><i className="fas fa-building" aria-hidden="true"></i>Empresas</h3>
              <span className="po-sub">rescisões com pagamento de hoje em diante e férias programadas deste mês em diante</span>
            </header>
            <div className="po-card-body">
              <div className="po-scroll">
                <table className="table table-hover po-table align-middle mb-0">
                  <thead><tr><th>Empresa</th><th>Operadora</th><th>E-mail do financeiro</th><th className="text-end">Rescisões</th><th className="text-end">Férias</th><th className="text-end">Urgentes (≤ 2 dias)</th><th></th></tr></thead>
                  <tbody>
                    {(visao?.clientes || []).map((c) => (
                      <tr key={c.cliente_id}>
                        <td className="fw-semibold">
                          {c.cliente}
                          {c.recorrencias_pendentes > 0 && (
                            <span className="badge ms-2" style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f1be48' }}
                              title="Recorrências do mês ainda não enviadas — as atrasadas também contam em Urgentes">
                              <i className="fas fa-arrows-rotate me-1" aria-hidden="true"></i>
                              {c.recorrencias_pendentes} recorrência{c.recorrencias_pendentes === 1 ? '' : 's'} pendente{c.recorrencias_pendentes === 1 ? '' : 's'}
                            </span>
                          )}
                        </td>
                        <td className="small text-muted">{c.operador || '—'}</td>
                        <td className="small">{c.emails || <span className="text-muted">não cadastrado</span>}</td>
                        <td className="text-end">{c.rescisoes || '—'}</td>
                        <td className="text-end">{c.ferias || '—'}</td>
                        <td className="text-end">{c.urgentes ? <span className="badge bg-danger">{c.urgentes}</span> : '—'}</td>
                        <td className="text-end"><button className="btn btn-sm btn-outline-primary" onClick={() => setClienteId(String(c.cliente_id))}>Abrir</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {clienteId && detalhe && (
          <>
            <section className="po-card po-enter mb-3">
              <header className="po-card-head">
                <h3><i className="fas fa-at" aria-hidden="true"></i>Financeiro da {detalhe.cliente}</h3>
                <button type="button" className="btn btn-sm btn-light border"
                  title="Cadastrar e editar as tarefas financeiras mensais desta empresa (FGTS, guias)"
                  onClick={() => { setRecModal(true); setRecForm(null); setRecConfirma(null); setRecErro(''); }}>
                  <i className="fas fa-arrows-rotate me-1" aria-hidden="true"></i>Recorrências
                </button>
                <span className="po-sub">cadastro próprio desta tela — vale para rescisão, férias e recorrências</span>
              </header>
              <div className="po-card-body">
                {/* Para e Cópia em destaque: é o endereço para onde o pagamento vai,
                    então precisa saltar aos olhos antes de a operadora clicar em enviar */}
                <div className="row g-3 align-items-end">
                  <div className="col-md-5">
                    <label className="form-label fw-bold mb-1" style={{ fontSize: '0.95rem', color: '#212529' }}>
                      <i className="fas fa-envelope me-2" style={{ color: '#0a6ea1' }} aria-hidden="true"></i>
                      Para <span className="text-danger">*</span>
                      <span className="text-muted fw-normal small ms-2">e-mail do financeiro do cliente · separe por ;</span>
                    </label>
                    <input type="text" placeholder="financeiro@cliente.com.br"
                      className={`form-control form-control-lg ${!(contato.emails || '').trim() ? 'is-invalid' : ''}`}
                      style={{ fontSize: '1rem', fontWeight: 600 }}
                      value={contato.emails} onChange={(e) => setContato((c) => ({ ...c, emails: e.target.value }))} />
                    {!(contato.emails || '').trim() && (
                      <div className="invalid-feedback d-block">
                        <i className="fas fa-triangle-exclamation me-1" aria-hidden="true"></i>
                        Sem e-mail do financeiro, nada é enviado para este cliente.
                      </div>
                    )}
                  </div>
                  <div className="col-md-5">
                    <label className="form-label fw-bold mb-1" style={{ fontSize: '0.95rem', color: '#212529' }}>
                      <i className="fas fa-copy me-2" style={{ color: '#6c757d' }} aria-hidden="true"></i>
                      Cópia
                      <span className="text-muted fw-normal small ms-2">opcional · separe por ;</span>
                    </label>
                    <input type="text" className="form-control form-control-lg" placeholder="quem mais deve receber"
                      style={{ fontSize: '1rem' }}
                      value={contato.copia} onChange={(e) => setContato((c) => ({ ...c, copia: e.target.value }))} />
                  </div>
                  <div className="col-md-2">
                    <button className="btn btn-primary btn-lg w-100" onClick={salvarContato} disabled={salvandoContato}>
                      <i className={`fas ${salvandoContato ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-1`}></i>Salvar contato
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* recorrências mensais ativas: uma linha por tarefa, com o estado do mês */}
            {(detalhe.recorrencias || []).some((r) => r.ativo) && (
              <section className="po-card po-enter mb-3">
                <header className="po-card-head">
                  <h3><i className="fas fa-arrows-rotate" aria-hidden="true"></i>Recorrências do mês</h3>
                  <span className="badge bg-light text-dark">{(detalhe.recorrencias || []).filter((r) => r.ativo && !r.enviado).length} pendente(s)</span>
                  <span className="po-sub">tarefas mensais com dia fixo (FGTS, guias) — o envio ao financeiro marca o mês como feito</span>
                </header>
                <div className="po-card-body">
                  <div className="po-scroll">
                    <table className="table table-hover po-table align-middle mb-0">
                      <thead><tr><th>Tarefa</th><th>Dia</th><th>Vence em</th><th>Situação do mês</th><th></th></tr></thead>
                      <tbody>
                        {(detalhe.recorrencias || []).filter((r) => r.ativo).map((r) => {
                          const sel = caso && caso.tipo === 'recorrencia' && String(caso.recorrencia_id) === String(r.id);
                          return (
                            <tr key={r.id} style={sel ? { background: '#e3f2fd' } : undefined}>
                              <td className="fw-semibold">
                                {r.titulo}
                                {!r.conferir_ia && <SeloIAOff />}
                              </td>
                              <td className="small text-muted">todo dia {r.dia}</td>
                              <td>{r.vence_em || '—'}</td>
                              <td><EstadoRecorrencia r={r} /></td>
                              <td className="text-end">
                                <button className={`btn btn-sm ${sel ? 'btn-primary' : r.enviado ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                                  onClick={() => escolherCaso({ tipo: 'recorrencia', recorrencia_id: r.id, titulo: r.titulo,
                                    dia: r.dia, vence_em: r.vence_em, conferir_ia: r.conferir_ia })}>
                                  <i className="fas fa-paper-plane me-1"></i>{sel ? 'Selecionado' : r.enviado ? 'Reenviar' : 'Preparar envio'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {/* rescisões e férias juntas, sem filtro: a operadora vê tudo que tem a comunicar */}
            {[['rescisao', detalhe.casos_rescisao || []], ['ferias', detalhe.casos_ferias || []]].map(([t, todos]) => {
              // enviado sai da lista por padrão; o toggle traz de volta (com check verde)
              const enviados = todos.filter((c) => c.enviado);
              const pendentes = todos.filter((c) => !c.enviado);
              const casos = mostrarEnviados[t] ? [...pendentes, ...enviados] : pendentes;
              return (
              <section key={t} className="po-card po-enter mb-3">
                <header className="po-card-head">
                  <h3><i className={`fas ${t === 'rescisao' ? 'fa-user-minus' : 'fa-umbrella-beach'}`} aria-hidden="true"></i>{TIPOS[t]} — pagamentos a comunicar</h3>
                  <span className="badge bg-light text-dark">{pendentes.length}</span>
                  {enviados.length > 0 && (
                    <button type="button" className="btn btn-sm btn-outline-success"
                      onClick={() => setMostrarEnviados((m) => ({ ...m, [t]: !m[t] }))}>
                      <i className={`fas ${mostrarEnviados[t] ? 'fa-eye-slash' : 'fa-circle-check'} me-1`} aria-hidden="true"></i>
                      {mostrarEnviados[t] ? 'ocultar enviados' : `${enviados.length} já enviado(s)`}
                    </button>
                  )}
                  <span className="po-sub">{t === 'rescisao' ? 'rescisões de hoje em diante: desligamento por vir ou prazo de pagamento ainda aberto (coluna Checklist mostra o item Financeiro)' : 'férias programadas na Convenia deste mês em diante — as ainda não aprovadas vêm marcadas'}</span>
                </header>
                <div className="po-card-body">
                  {casos.length === 0 ? (
                    <div className="text-muted small py-2">
                      Nada pendente em {TIPOS[t].toLowerCase()}{enviados.length > 0 ? ` — ${enviados.length} já enviado(s) ao financeiro.` : '.'}</div>
                  ) : (
                    <div className="po-scroll">
                      <table className="table table-hover po-table align-middle mb-0">
                        <thead><tr><th>Colaborador</th><th>Cargo</th><th>{casos[0].data_evento_rotulo || 'Data'}</th><th>Pagar até</th><th>Prazo</th>{t === 'rescisao' && <th>Checklist</th>}<th></th></tr></thead>
                        <tbody>
                          {casos.map((c) => {
                            const d = c.dias_para_prazo;
                            const cor = d == null ? '#6c757d' : d < 0 ? '#a11d2b' : d <= 2 ? '#b5560e' : '#116b47';
                            const sel = caso && caso.tipo === t && String(caso.convenia_id) === String(c.convenia_id)
                              && (caso.referencia || '') === (c.referencia || '');
                            return (
                              <tr key={`${c.convenia_id}-${c.referencia || c.data_evento || ""}`} style={sel ? { background: '#e3f2fd' } : undefined}>
                                <td className="fw-semibold">
                                  {c.colaborador}
                                  {c.enviado && (
                                    <span className="badge ms-2" style={{ background: '#e8f5e9', color: '#116b47', border: '1px solid #116b47' }}
                                      title={`Comunicado ao financeiro em ${c.enviado.em}${c.enviado.por ? ` por ${c.enviado.por}` : ''}`}>
                                      <i className="fas fa-circle-check me-1" aria-hidden="true"></i>enviado {c.enviado.em}
                                    </span>
                                  )}
                                  {c.aprovada === false && (
                                    <span className="badge ms-2" style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f1be48' }}
                                      title="Solicitação ainda não aprovada na Convenia — o pagamento só vale depois da aprovação">
                                      {c.status_rotulo || 'em aprovação'}
                                    </span>
                                  )}
                                </td>
                                <td className="small text-muted">{c.cargo || '—'}</td>
                                <td>{c.data_evento || '—'}</td>
                                <td>{c.prazo_pagamento || '—'}</td>
                                <td style={{ color: cor, fontWeight: 600 }} title={c.prazo_regra || ''}>
                                  {d == null ? '—' : d < 0 ? `${-d} dia(s) de atraso` : d === 0 ? 'vence hoje' : `faltam ${d} dia(s)`}
                                </td>
                                {t === 'rescisao' && <td className="small">{c.checklist_financeiro}</td>}
                                <td className="text-end">
                                  <button className={`btn btn-sm ${sel ? 'btn-primary' : c.enviado ? 'btn-outline-secondary' : 'btn-outline-primary'}`} onClick={() => escolherCaso(c)}>
                                    <i className="fas fa-paper-plane me-1"></i>{sel ? 'Selecionado' : c.enviado ? 'Reenviar' : 'Preparar envio'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
              );
            })}

            {caso && (
              <section className="po-card po-enter mb-3" style={{ borderLeft: `4px solid ${bloqueado ? '#a11d2b' : '#00b5cc'}` }}>
                <header className="po-card-head">
                  <h3><i className="fas fa-file-shield" aria-hidden="true"></i>
                    Envio — {caso.tipo === 'recorrencia' ? <>{TIPOS.recorrencia} · {caso.titulo}</> : <>{TIPOS[caso.tipo]} de {caso.colaborador}</>}</h3>
                  {caso.tipo === 'recorrencia'
                    ? (
                      <>
                        <span className="po-sub">todo dia {caso.dia} · vence em {caso.vence_em || '—'}</span>
                        {/* o toggle de IA da recorrência, somente leitura: aqui só informa */}
                        {caso.conferir_ia
                          ? <span className="badge" style={{ background: '#e6f7ef', color: '#116b47', border: '1px solid #116b4733' }}><i className="fas fa-wand-magic-sparkles me-1" aria-hidden="true"></i>anexo conferido por IA</span>
                          : <SeloIAOff />}
                      </>
                    )
                    : <span className="po-sub">{caso.data_evento_rotulo}: {caso.data_evento || '—'} · pagar até {caso.prazo_pagamento || '—'}</span>}
                </header>
                <div className="po-card-body">
                  <h6 className="fw-bold text-muted border-bottom pb-1 mb-2"><i className="fas fa-paperclip me-2"></i>Documentos do pagamento</h6>
                  <label className={`btn btn-sm btn-outline-primary ${subindo ? 'disabled' : ''}`}>
                    <i className={`fas ${subindo ? 'fa-spinner fa-spin' : 'fa-file-arrow-up'} me-2`}></i>
                    {subindo
                      ? (caso.tipo === 'recorrencia' && !caso.conferir_ia ? 'Anexando...' : 'Conferindo o documento...')
                      : caso.tipo === 'recorrencia' ? 'Anexar PDF (guia, boleto, comprovante)' : 'Anexar PDF (TRCT, guias, recibo de férias)'}
                    <input type="file" accept="application/pdf" hidden onChange={anexar} disabled={subindo} />
                  </label>
                  {anexos.length === 0 && (
                    <div className="text-muted small mt-2">
                      Nenhum documento anexado ainda.{caso.tipo === 'recorrencia' && !caso.conferir_ia
                        ? ' A conferência por IA está desligada para esta recorrência: confira o PDF você mesma antes de enviar.'
                        : ' Ao anexar, o PDF é conferido antes de liberar o envio.'}
                    </div>
                  )}
                  {anexos.map((a) => (
                    <div key={a.id} className="d-flex align-items-start gap-3 mt-2 p-2 rounded border" style={{ background: a.laudo?.bloqueia ? '#fdeaec' : '#f8f9fa' }}>
                      <i className="fas fa-file-pdf fa-lg mt-1" style={{ color: '#a11d2b' }}></i>
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">
                          <a href={`${API}/api/bporh/financeiro/${clienteId}/anexo/${a.id}/`} target="_blank" rel="noreferrer">{a.nome_original}</a>
                          <span className="text-muted ms-2">{a.tamanho ? `${Math.round(a.tamanho / 1024)} KB` : ''}</span>
                        </div>
                        <Laudo laudo={a.laudo} />
                      </div>
                      <button className="btn btn-sm btn-outline-danger" title="Remover este anexo" onClick={() => removerAnexo(a)}><i className="fas fa-trash"></i></button>
                    </div>
                  ))}

                  {bloqueado && (
                    <div className="alert alert-danger mt-3 mb-0 py-2">
                      <i className="fas fa-ban me-2"></i><b>Envio bloqueado.</b> Um dos documentos não é desta pessoa ou desta empresa.
                      Remova o anexo e confira o arquivo antes de tentar de novo.
                    </div>
                  )}

                  {!bloqueado && anexos.length > 0 && !envio?.previa && (
                    <div className="mt-3">
                      <button className="btn btn-primary" onClick={() => montarPrevia(null)} disabled={envio?.carregando}>
                        <i className={`fas ${envio?.carregando ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>Montar prévia do e-mail
                      </button>
                      {envio?.erro && <span className="text-danger small ms-3">{envio.erro}</span>}
                    </div>
                  )}

                  {previaEmMemoria && (
                    <div className="mt-3">
                      <h6 className="fw-bold text-muted border-bottom pb-1 mb-2"><i className="fas fa-envelope-open-text me-2"></i>Prévia do e-mail</h6>
                      <div className="row g-2">
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold mb-1">Enviar como (operadora)</label>
                          <select className="form-select form-select-sm" value={envio.form.operador_id} onChange={(e) => mudarForm({ operador_id: e.target.value })}>
                            <option value="">— escolha —</option>
                            {(envio.previa.operadores || []).map((o) => <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{o.email ? ` — ${o.email}` : ''}{!o.pronto ? ' (sem e-mail/senha)' : ''}</option>)}
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold mb-1">Para</label>
                          <input type="text" className="form-control form-control-sm" value={envio.form.destinatarios} onChange={(e) => mudarForm({ destinatarios: e.target.value })} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold mb-1">Cópia</label>
                          <input type="text" className="form-control form-control-sm" value={envio.form.copia} onChange={(e) => mudarForm({ copia: e.target.value })} />
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold mb-1">Assunto</label>
                          <input type="text" className="form-control form-control-sm" value={envio.form.assunto} onChange={(e) => mudarForm({ assunto: e.target.value })} />
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold mb-1">
                            Texto da mensagem
                            {envio.previa.template_proprio && <span className="badge bg-info text-dark ms-2">texto próprio desta empresa</span>}
                            <span className="text-muted fw-normal ms-2">editar aqui salva como padrão da empresa ao enviar</span>
                          </label>
                          <EditorEmail valor={envio.form.conteudo_html} onChange={(vv) => mudarForm({ conteudo_html: vv })} altura={260} />
                        </div>
                      </div>
                      {envio.previaDesatualizada ? (
                        <div className="alert alert-warning py-2 mt-2 mb-2 small">
                          <i className="fas fa-rotate me-2"></i>Você alterou o e-mail — monte a prévia de novo para ver como vai sair.
                          <button className="btn btn-sm btn-outline-dark ms-3" onClick={() => montarPrevia(envio.form)}>Atualizar prévia</button>
                        </div>
                      ) : (
                        <div className="border rounded mt-2" style={{ overflow: 'hidden' }}>
                          <div className="small px-3 py-2 bg-light border-bottom">
                            <b>De:</b> {envio.previa.remetente} · <b>Para:</b> {envio.form.destinatarios || '—'}{envio.form.copia ? <> · <b>Cc:</b> {envio.form.copia}</> : null}
                            {' '}· <b>Anexos:</b> {(envio.previa.anexos || []).map((a) => a.nome).join(', ') || 'nenhum'}
                          </div>
                          <iframe title="Prévia" srcDoc={envio.previa.corpo_previa} sandbox="" style={{ width: '100%', height: 420, border: 'none', background: '#f4f6fa' }} />
                        </div>
                      )}
                      {!envio.previaDesatualizada && (envio.previa.datas_estranhas || []).length > 0 && (
                        <div className="alert alert-danger py-2 mt-2 mb-0 small">
                          <i className="fas fa-calendar-xmark me-2"></i>
                          <b>O texto tem data(s) que não são deste colaborador:</b> {envio.previa.datas_estranhas.join(', ')}.
                          {' '}O período dele é <b>{envio.previa.datas_do_caso?.periodo || '—'}</b> e o prazo de pagamento, <b>{envio.previa.datas_do_caso?.prazo || '—'}</b>.
                          {' '}Corrija o texto (ou clique em restaurar o texto padrão) antes de enviar.
                          <div className="form-check mt-1">
                            <input className="form-check-input" type="checkbox" id="confDatas" checked={!!envio.form.confirmarDatas}
                              onChange={(e) => setEnvio((s) => ({ ...s, form: { ...s.form, confirmarDatas: e.target.checked } }))} />
                            <label className="form-check-label" htmlFor="confDatas">Conferi: essas datas estão certas e podem ir no e-mail</label>
                          </div>
                        </div>
                      )}
                      {ressalvas.length > 0 && (
                        <div className="form-check mt-2">
                          <input className="form-check-input" type="checkbox" id="confRessalvas" checked={!!envio.form.confirmar} onChange={(e) => setEnvio((s) => ({ ...s, form: { ...s.form, confirmar: e.target.checked } }))} />
                          <label className="form-check-label small" htmlFor="confRessalvas" style={{ color: '#8a6d00' }}>
                            <b>Conferi o documento e confirmo o envio apesar das ressalvas:</b> {ressalvas.join('; ')}
                          </label>
                        </div>
                      )}
                      {envio.erro && <div className="alert alert-danger py-2 mt-2 mb-0 small">{envio.erro}</div>}
                      <div className="d-flex gap-2 mt-3 justify-content-end">
                        <button className="btn btn-secondary" onClick={() => setEnvio(null)} disabled={envio.enviando}><i className="fas fa-times me-2"></i>Cancelar</button>
                        <button className="btn btn-success" onClick={enviar}
                          disabled={envio.enviando || envio.previaDesatualizada || !envio.form.destinatarios.trim() || !envio.form.operador_id || (ressalvas.length > 0 && !envio.form.confirmar)
                            || ((envio.previa?.datas_estranhas || []).length > 0 && !envio.form.confirmarDatas)}
                          title={envio.previaDesatualizada ? 'Atualize a prévia antes de enviar' : 'Envia exatamente o que está na prévia'}>
                          <i className={`fas ${envio.enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>{envio.enviando ? 'Enviando...' : 'Enviar ao financeiro'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ─── Modal: gestão das recorrências mensais ─── */}
            {recModal && (
              <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
                onClick={(e) => { if (e.target === e.currentTarget) setRecModal(false); }}>
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content shadow-lg">
                    <div className="modal-header bg-white">
                      <div>
                        <h5 className="modal-title" style={{ color: '#212529' }}>
                          <i className="fas fa-arrows-rotate me-2" style={{ color: '#00b5cc' }} aria-hidden="true"></i>
                          Recorrências — {detalhe.cliente}
                        </h5>
                        <div className="small" style={{ color: '#6c757d' }}>
                          Tarefas financeiras mensais com dia fixo (sem antecipação para dia útil). O envio pelo Financeiro marca o mês como feito.
                        </div>
                      </div>
                      <button type="button" className="btn-close" onClick={() => setRecModal(false)} aria-label="Fechar"></button>
                    </div>
                    <div className="modal-body">
                      {recErro && <div className="alert alert-danger py-2 small" onClick={() => setRecErro('')} role="button"><i className="fas fa-circle-exclamation me-2"></i>{recErro}</div>}
                      {(detalhe.recorrencias || []).length === 0
                        ? <div className="text-muted small mb-3">Nenhuma recorrência cadastrada para esta empresa.</div>
                        : (
                          <table className="table table-sm align-middle mb-3">
                            <thead className="table-light">
                              <tr><th>Título</th><th>Dia</th><th className="text-center">IA</th><th className="text-center">Ativa</th><th>Situação do mês</th><th className="text-end">Ações</th></tr>
                            </thead>
                            <tbody>
                              {(detalhe.recorrencias || []).map((r) => (
                                <tr key={r.id} style={!r.ativo ? { opacity: 0.55 } : undefined}>
                                  <td>
                                    <div className="fw-semibold">{r.titulo}</div>
                                    {r.instrucao && <div className="small text-muted">{r.instrucao}</div>}
                                  </td>
                                  <td className="small">todo dia {r.dia}</td>
                                  <td className="text-center" title={r.conferir_ia ? 'Anexo conferido por IA' : 'IA desligada: anexo sem conferência'}>
                                    {r.conferir_ia
                                      ? <i className="fas fa-check" style={{ color: '#116b47' }} aria-label="IA ligada"></i>
                                      : <i className="fas fa-xmark" style={{ color: '#a11d2b' }} aria-label="IA desligada"></i>}
                                  </td>
                                  <td className="text-center">
                                    {r.ativo
                                      ? <i className="fas fa-check" style={{ color: '#116b47' }} aria-label="Ativa"></i>
                                      : <i className="fas fa-xmark" style={{ color: '#a11d2b' }} aria-label="Inativa"></i>}
                                  </td>
                                  <td>{r.ativo ? <EstadoRecorrencia r={r} /> : <span className="text-muted small">inativa — fora da cobrança</span>}</td>
                                  <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                                    {recConfirma === r.id ? (
                                      <span className="small">
                                        <span style={{ color: '#a11d2b', fontWeight: 600 }}>Excluir?</span>
                                        <button className="btn btn-sm btn-danger ms-2" disabled={recSalvando} onClick={() => excluirRecorrencia(r.id)}>
                                          <i className={`fas ${recSalvando ? 'fa-spinner fa-spin' : 'fa-trash'} me-1`}></i>Sim
                                        </button>
                                        <button className="btn btn-sm btn-light border ms-1" disabled={recSalvando} onClick={() => setRecConfirma(null)}>Não</button>
                                      </span>
                                    ) : (
                                      <>
                                        <button className="btn btn-sm btn-outline-primary" title="Editar esta recorrência"
                                          onClick={() => { setRecConfirma(null); setRecForm({ id: r.id, titulo: r.titulo, dia: r.dia, conferir_ia: !!r.conferir_ia, instrucao: r.instrucao || '', ativo: !!r.ativo }); }}>
                                          <i className="fas fa-pen"></i>
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger ms-1" title="Excluir esta recorrência"
                                          onClick={() => setRecConfirma(r.id)}>
                                          <i className="fas fa-trash"></i>
                                        </button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                      {!recForm ? (
                        <button className="btn btn-sm btn-primary"
                          onClick={() => { setRecConfirma(null); setRecForm({ titulo: '', dia: '', conferir_ia: true, instrucao: '', ativo: true }); }}>
                          <i className="fas fa-plus me-1"></i>Nova recorrência
                        </button>
                      ) : (
                        <div className="border rounded p-3" style={{ background: '#f8f9fa' }}>
                          <h6 className="fw-bold mb-2" style={{ color: '#212529' }}>
                            <i className={`fas ${recForm.id ? 'fa-pen' : 'fa-plus'} me-2`} style={{ color: '#00b5cc' }}></i>
                            {recForm.id ? 'Editar recorrência' : 'Nova recorrência'}
                          </h6>
                          <div className="row g-2">
                            <div className="col-md-8">
                              <label className="form-label small fw-semibold mb-1">Título <span className="text-danger">*</span></label>
                              <input type="text" className="form-control form-control-sm" maxLength={120} placeholder="ex.: Guia do FGTS Digital"
                                value={recForm.titulo} onChange={(e) => setRecForm((s) => ({ ...s, titulo: e.target.value }))} />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label small fw-semibold mb-1">Dia do mês (1–31) <span className="text-danger">*</span></label>
                              <input type="number" min={1} max={31} className="form-control form-control-sm"
                                value={recForm.dia} onChange={(e) => setRecForm((s) => ({ ...s, dia: e.target.value }))} />
                            </div>
                            <div className="col-12">
                              <label className="form-label small fw-semibold mb-1">
                                Instrução para a IA
                                <span className="text-muted fw-normal ms-2">o que o documento anexado deve ser</span>
                              </label>
                              <input type="text" className="form-control form-control-sm" maxLength={300}
                                placeholder="ex.: guia do FGTS Digital desta empresa"
                                value={recForm.instrucao} onChange={(e) => setRecForm((s) => ({ ...s, instrucao: e.target.value }))} />
                            </div>
                            <div className="col-12 d-flex gap-4 mt-2">
                              <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" id="recIA" checked={recForm.conferir_ia}
                                  onChange={(e) => setRecForm((s) => ({ ...s, conferir_ia: e.target.checked }))} />
                                <label className="form-check-label small" htmlFor="recIA">
                                  Conferir anexo com IA
                                  <span className="text-muted ms-1">(desligada, o anexo vai sem conferência, com o selo "IA off")</span>
                                </label>
                              </div>
                              <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" id="recAtiva" checked={recForm.ativo}
                                  onChange={(e) => setRecForm((s) => ({ ...s, ativo: e.target.checked }))} />
                                <label className="form-check-label small" htmlFor="recAtiva">Ativa</label>
                              </div>
                            </div>
                          </div>
                          {recForm.dia !== '' && !(Number(recForm.dia) >= 1 && Number(recForm.dia) <= 31) && (
                            <div className="small mt-2" style={{ color: '#a11d2b' }}>
                              <i className="fas fa-triangle-exclamation me-1"></i>O dia precisa estar entre 1 e 31 (mês mais curto usa o último dia).
                            </div>
                          )}
                          <div className="d-flex gap-2 mt-3 justify-content-end">
                            <button className="btn btn-sm btn-light border" onClick={() => setRecForm(null)} disabled={recSalvando}>Cancelar</button>
                            <button className="btn btn-sm btn-primary" onClick={salvarRecorrencia} disabled={recSalvando || !recFormValido}>
                              <i className={`fas ${recSalvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-1`}></i>Salvar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="modal-footer bg-white d-flex justify-content-between">
                      <span className="small text-muted">O dia é fixo, sem antecipação para dia útil. Pendentes aparecem na visão geral e no Painel do Operador.</span>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => setRecModal(false)}>Fechar</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <section className="po-card po-enter">
          <header className="po-card-head">
            <h3><i className="fas fa-clock-rotate-left" aria-hidden="true"></i>Envios — últimos 90 dias{clienteSel ? ` · ${clienteSel.cliente}` : ''}</h3>
            <span className="badge bg-light text-dark">{historico.length}</span>
          </header>
          <div className="po-card-body">
            {historico.length === 0 ? <div className="text-muted small py-2">Nenhum envio no período.</div> : (
              <div className="po-scroll">
                <table className="table table-hover po-table align-middle mb-0">
                  <thead><tr><th>Quando</th>{!clienteSel && <th>Empresa</th>}<th>Tipo</th><th>Colaborador</th><th>Para</th><th>Anexos</th><th>Por</th></tr></thead>
                  <tbody>
                    {historico.map((h) => (
                      <tr key={h.id}>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>{h.enviado_em}</td>
                        {!clienteSel && <td className="small">{h.cliente}</td>}
                        <td className="small">{h.tipo_rotulo}{h.com_ressalva && <i className="fas fa-triangle-exclamation ms-1" style={{ color: '#8a6d00' }} title="Enviado com ressalvas confirmadas"></i>}</td>
                        <td className="fw-semibold small">{h.colaborador}</td>
                        <td className="small text-muted">{h.destinatarios}</td>
                        <td className="small">{h.anexos}</td>
                        <td className="small">{h.enviado_por}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
